import React, { memo } from 'react';

import { COLORS } from 'Components/foundation';
import SFlagGroup from 'Components/structural/SFlagGroup/SFlagGroup';
import UIcon from 'Components/unit/UIcon/UIcon';
import { UIconButton } from 'Components/unit/UIconButton/UIconButton';
import UStatusIndicator from 'Components/unit/UStatusIndicator/UStatusIndicator';
import { Languages } from 'Models/Enum';
import { mapActivityStatusLabel } from 'Pages/Activity/activity.mapper';
import { getLanguageFromCode } from 'Services/language/languageService';

import { ActivityImage } from './Components/ActivityImage';
import ActivityOptions from './Components/ActivityOptions';
import ActivityTitle from './Components/ActivityTitle';
import ActivityTypeAndId from './Components/ActivityTypeAndId';
import CalendarSection from './Components/CalendarSection';
import TargetAudience from './Components/TargetAudience';
import UserEvents from './Components/UserEvents';
import { useActivityActions, useActivityData, useActivityUI } from './Hooks';
import { ACTIVITY_STATUS_TYPE_MAP } from './Item.constants';
import styles, { getStyleWrapper } from './Item.styles';

export type ItemProps = {
  activityId: number;
};

const Item: React.FC<ItemProps> = ({ activityId }) => {
  const { activity, translatedTitle, coverImage: image, formattedDates, activityTypeMap } = useActivityData(activityId);

  const {
    isHover,
    isOptionOpen,
    wrapperRef,
    handleHover,
    handleLeave,
    handleOptionClick,
    handleOutsideClick,
    handleClickOption,
  } = useActivityUI();

  if (!activity) return null;

  const { handleClickItem, handleSelectLanguage, options, archiveWarningModal } = useActivityActions(
    activityId,
    activity.status,
    activity?.type,
  );

  const { type, status, audience, languages, likes, views } = activity;

  return (
    <div>
      <div
        style={{
          ...styles.wrapper,
          ...getStyleWrapper(isHover),
        }}
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
        onClick={handleClickItem}
        data-test-id="activity-list-item"
      >
        <ActivityImage image={image} />

        <div style={styles.containerInfos}>
          <div style={styles.containerHeader}>
            <div style={styles.containerHeaderLeft}>
              <ActivityTypeAndId activityId={activityId} type={type} activityTypeMap={activityTypeMap} />
              <ActivityTitle translatedTitle={translatedTitle} />
              <UStatusIndicator
                type={ACTIVITY_STATUS_TYPE_MAP[status]}
                text={mapActivityStatusLabel(status)}
                size="medium"
              />
            </div>

            <div style={styles.containerHeaderRight}>
              {type === 'campaign' && (
                <UIconButton
                  icon="analytics"
                  disabled
                  onClick={() => {}}
                  stopClickPropagation
                  ghost
                  type="accentuated"
                  style={styles.analyticsIcon}
                  data-test-id="analytics-button"
                />
              )}

              <ActivityOptions
                options={options}
                isOptionOpen={isOptionOpen}
                wrapperRef={wrapperRef}
                handleOptionClick={handleOptionClick}
                handleOutsideClick={handleOutsideClick}
                handleClickOption={handleClickOption}
                activityId={activityId}
              />
            </div>
          </div>

          <div style={styles.containerFooter}>
            <div style={styles.containerFooterLeft}>
              <CalendarSection formattedDates={formattedDates} />

              <div style={styles.containerTarget} data-test-id="target-section">
                <UIcon name="target" size={16} color={COLORS.GREY_DARK.DEFAULT} style={styles.iconFooter} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  <TargetAudience audience={audience} />
                </div>
              </div>
            </div>
            <div style={styles.containerFooterRight}>
              <SFlagGroup
                translationStatuses={languages.map((code) => ({
                  countryCode: Languages[code as keyof typeof Languages],
                  languageLabel: getLanguageFromCode(code as keyof typeof Languages).languageName,
                  onClick: (event) => handleSelectLanguage(event, code),
                }))}
                disabledFlagsHover={false}
              />

              <UserEvents likes={likes} views={views} type={type} />
            </div>
          </div>
        </div>
      </div>

      {archiveWarningModal}
    </div>
  );
};

export default memo(Item);
